// THE SHIFT JOIN — one implementation, because §9.7 dates a shift by the day it
// STARTED and half the area kept forgetting.
//
// A waste event carries a `shift_instance_id` and nothing else about the shift.
// The operator's word for that instance is its shift-definition CODE, and the
// only read in the product that maps id → code is module 04's own scoreboard
// (`/v1/shifts/scoreboard`, same audience: every §4 S2/S4 role holds
// `production.scoreboard.read`). There is no shift-instances endpoint; when the
// board cannot name an instance, nothing can, and the screens say so as an
// IDENTIFIER (`RefLabel`) rather than printing a uuid where a name belongs.
//
// THE TWO-DATE RULE. §9.7 dates a shift by the day it STARTED, so a night shift
// running 22:00 → 06:00 lives on YESTERDAY's board all through the small hours.
// A screen that polls only the picked day therefore cannot name the shift that
// is running while it is being looked at. The wall board polls two dates for
// exactly this reason (spec 04 §4 row 12), the ledger was fixed to do the same
// (M05-S01, caught at 00:3x Almaty, 2026-08-10) — and the shift SCOREBOARD, the
// screen whose entire purpose is shift attribution, was never fixed. Measured
// (surface review, 2026-08-13): a 06:17 Almaty event whose station screen said
// `NIGHT / 2026-08-12` was headed `019ff808` on the board, because the board
// polled 2026-08-13 alone.
//
// It is one hook now. A third screen cannot reintroduce the one-date form
// without deleting this file.

import { shortId } from "../../../../lib/short-id";
import { useMemo } from "react";
import { useShiftScoreboard } from "../../production/hooks/production-queries";

/** One board row, reduced to the two fields a label is built from. */
export interface ShiftIdentityRow {
  readonly shift_instance_id: string;
  readonly shift_definition_code: string;
  readonly shift_date: string;
}

/** The ISO date one day before an ISO date (pure UTC calendar arithmetic —
 * these are business dates, not instants). */
export function isoDayBefore(dateIso: string): string {
  const [y, m, d] = dateIso.split("-").map(Number);
  const prior = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) - 1));
  return `${String(prior.getUTCFullYear()).padStart(4, "0")}-${String(prior.getUTCMonth() + 1).padStart(2, "0")}-${String(prior.getUTCDate()).padStart(2, "0")}`;
}

/**
 * Fold two days of board rows into `instance id → label`, first mention wins.
 *
 * Pure and exported so the two-date rule has a test that does not need a
 * network. The label is `CODE · YYYY-MM-DD`: the code alone is ambiguous the
 * moment two days are in play, which is the whole point of reading two.
 */
export function shiftLabelIndex(
  ...boards: readonly (readonly ShiftIdentityRow[] | undefined)[]
): Map<string, string> {
  const seen = new Map<string, string>();
  for (const board of boards) {
    for (const row of board ?? []) {
      if (!seen.has(row.shift_instance_id)) {
        seen.set(row.shift_instance_id, `${row.shift_definition_code} · ${row.shift_date}`);
      }
    }
  }
  return seen;
}

export interface ShiftLabels {
  /** `[{ id, label }]` for a picker, in board order. */
  readonly options: readonly { id: string; label: string }[];
  /** The operator's word for an instance, or `undefined` when no board names it. */
  readonly labelOf: (id: string | null | undefined) => string | undefined;
  /** True while either board is still in flight — the `RefLabel` resolving state. */
  readonly isPending: boolean;
}

/**
 * Resolve shift instances for a (facility, business date), reading the picked
 * day AND the day before it.
 *
 * `enabled` is the facility: a shift instance is a (facility, date) fact, and
 * an all-tenant board would be ambiguous across same-coded definitions.
 */
export function useShiftLabels(
  facilityId: string | undefined,
  dateIso: string,
  extraIds: readonly string[] = [],
): ShiftLabels {
  const enabled = facilityId !== undefined && facilityId.length > 0;
  const priorDate = useMemo(() => isoDayBefore(dateIso), [dateIso]);
  const facilityParam = enabled ? { facility_id: facilityId } : {};

  const board = useShiftScoreboard({ shift_date: dateIso, ...facilityParam }, enabled);
  const priorBoard = useShiftScoreboard({ shift_date: priorDate, ...facilityParam }, enabled);

  // BOTH boards are dependencies. `priorBoard.data` was once missing from the
  // ledger's dependency list, so whenever the prior day's board resolved AFTER
  // the picked day's, its rows never entered the options — and in the
  // 00:00–05:00 Almaty window (picked day young, its own board EMPTY and
  // therefore first to answer) the night shift was reliably absent.
  const index = useMemo(
    () => shiftLabelIndex(board.data?.rows, priorBoard.data?.rows),
    [board.data, priorBoard.data],
  );

  const options = useMemo(() => {
    const out = [...index.entries()].map(([id, label]) => ({ id, label }));
    // An id the caller is ALREADY filtered by (from the URL, or from a drill
    // link off another screen) has to render as the applied filter it is, even
    // when it is not on either board — or the select would silently claim "any
    // shift" while the ledger is filtered to one.
    for (const id of extraIds) {
      if (id.length > 0 && !index.has(id)) out.push({ id, label: shortId(id) });
    }
    return out;
  }, [index, extraIds]);

  return {
    options,
    labelOf: (id) => (id === null || id === undefined ? undefined : index.get(id)),
    isPending: enabled && (board.isPending || priorBoard.isPending),
  };
}
